import React, { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import {
  FaGraduationCap,
  FaLaptopCode,
  FaUserShield,
  FaUsers,
  FaCogs,
  FaHeadset,
  FaArrowRight,
  FaChartBar,
  FaShieldAlt,
  FaChevronLeft,
  FaChevronRight,
} from "react-icons/fa";
import { FaLinkedin, FaPhone, FaEnvelope } from "react-icons/fa";
import "./About.scss";
import founderImage from "../../assets/images/siddharth.jpg";
import Button from "../../components/Button/Button";
import { useNavigate } from "react-router-dom";

const AboutUs = () => {
  const navigate = useNavigate();
  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.3,
      },
    },
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { duration: 0.5 },
    },
  };

  const values = [
    {
      icon: <FaUsers />,
      title: "Client-Centric Approach",
      description:
        "We build solutions tailored to the specific needs of each educational institution.",
    },
    {
      icon: <FaCogs />,
      title: "Innovation",
      description:
        "We continuously evolve our ERP platform with cutting-edge technologies and features.",
    },
    {
      icon: <FaUserShield />,
      title: "Security",
      description:
        "We implement robust security measures to protect sensitive educational data.",
    },
    {
      icon: <FaHeadset />,
      title: "Support",
      description:
        "We provide ongoing technical support and training to ensure smooth system operation.",
    },
  ];

  return (
    <div className="about-page">
      {/* Hero Section */}
      <section className="about-hero">
        <div className="about-hero__content">
          <motion.h1
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            Transforming Education Through Technology
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            Lancehawks Technologies is revolutionizing educational management
            with our comprehensive ERP solutions.
          </motion.p>
          <div className="hero-buttons">
            <a
              href="https://lancehawks.com"
              target="_blank"
              rel="noopener noreferrer"
            >
              <Button
                type="secondary"
                text="Visit Lancehawks"
                icon={<FaArrowRight />}
              />
            </a>
            <Button
              type="outline"
              text="Talk to Sales"
              icon={<FaPhone />}
              onClick={() => (window.location.href = "tel:+919915697333")}
            />
            <Button
              type="secondary"
              text="Contact Us"
              icon={<FaArrowRight />}
              onClick={() => navigate("/contact")}
            />
          </div>
        </div>
      </section>

      {/* Company Overview */}
      <section className="about-section">
        <div className="about-container">
          <motion.div
            className="section-header"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            {/* <span className="section-badge">Who We Are</span> */}
            <h2>Company Overview</h2>
          </motion.div>

          <div className="overview-content">
            <motion.div
              className="overview-text"
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              <p>
                Lancehawks Technologies Private Limited is a forward-thinking
                technology innovator dedicated to crafting exceptional digital
                experiences. We specialize in developing bespoke,
                enterprise-grade software solutions, sophisticated web
                platforms, and intuitive mobile applications tailored to the
                unique challenges faced by our diverse clientele across multiple
                industries.
              </p>
              <p>
                Our multidisciplinary team combines deep domain expertise with
                cutting-edge development capabilities to deliver transformative
                solutions that drive operational excellence and competitive
                advantage. We take pride in our collaborative approach, working
                closely with each client to understand their vision and
                translate it into powerful digital tools that exceed
                expectations and generate measurable business value.
              </p>
            </motion.div>

            <motion.div
              className="vision-mission"
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              <div className="vision">
                <h3>Our Vision</h3>
                <p>
                  To build world-class educational software that empowers
                  institutions with seamless digital transformation, data
                  security, and operational excellence.
                </p>
              </div>
              <div className="mission">
                <h3>Our Mission</h3>
                <p>
                  To revolutionize the education sector with state-of-the-art
                  ERP solutions tailored to schools, colleges, and universities,
                  enabling them to focus on what matters most: education.
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      <ERPSolutionsSection />

      {/* Our Values */}
      <section className="about-section">
        <div className="about-container">
          <motion.div
            className="section-header"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            {/* <span className="section-badge">How We Work</span> */}
            <h2>Our Values</h2>
          </motion.div>

          <div className="values-container">
            {values.map((value, index) => (
              <motion.div
                key={index}
                className="value-item"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <div className="value-icon">{value.icon}</div>
                <h3>{value.title}</h3>
                <p>{value.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <motion.div
        className="leadership-section"
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.5 }}
      >
        <motion.div
          className="section-header"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
        >
          {/* <span className="section-badge">What We Do</span> */}
          <h2>Leadership</h2>
        </motion.div>
        <div className="leader-card">
          <div className="leader-profile">
            <div className="leader-image">
              <img src={founderImage} alt="Siddharth Rai - Founder & CEO" />
            </div>
            <div className="leader-credentials">
              <h3>Siddharth Rai</h3>
              <p className="leader-title">Founder & Chief Executive Officer</p>
              <div className="leader-social">
                <a
                  href="https://www.linkedin.com/in/siddrai7/"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="LinkedIn"
                >
                  <FaLinkedin />
                </a>
                <a
                  href="tel:+919915697333"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Twitter"
                >
                  <FaPhone />
                </a>
                <a href="mailto:siddharth@lancehawks.com" aria-label="Email">
                  <FaEnvelope />
                </a>
              </div>
            </div>
          </div>
          <div className="leader-content">
            <p className="leader-bio">
              Siddharth Rai is a visionary technologist and strategic business
              leader with extensive expertise in enterprise software
              development, cybersecurity, and educational technology. With a
              background in computer science and business administration, he
              combines technical acumen with entrepreneurial insight to drive
              Lancehawks' innovation agenda.
            </p>
            <p className="leader-achievements">
              Under his leadership, Lancehawks Technologies has evolved into a
              distinguished provider of cutting-edge ERP solutions that address
              the complex operational challenges faced by modern educational
              institutions. His forward-thinking approach to technology
              development has established new standards for efficiency,
              security, and user experience in educational management systems.
            </p>
            <div className="leader-quote">
              <blockquote>
                "Our mission extends beyond software development—we're creating
                digital ecosystems that empower educational institutions to
                focus on what truly matters: providing exceptional learning
                experiences."
              </blockquote>
            </div>
          </div>
        </div>
      </motion.div>

      {/* CTA Section */}
      <section className="about-cta">
        <div className="about-container">
          <motion.div
            className="cta-content"
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <h2>Ready to Transform Your Institution?</h2>
            <p>
              Get in touch with us to learn how our ERP solution can streamline
              your operations.
            </p>
            <div className="cta-buttons">
              <a
                href="https://lancehawks.com"
                target="_blank"
                rel="noopener noreferrer"
              >
                <Button
                  type="secondary"
                  text="Visit Lancehawks"
                  icon={<FaArrowRight />}
                />
              </a>
              <Button
                type="outline"
                text="Talk to Sales"
                icon={<FaPhone />}
                onClick={() => (window.location.href = "tel:+919915697333")}
              />
              <Button
                type="secondary"
                text="Contact Us"
                icon={<FaArrowRight />}
                onClick={() => navigate("/contact")}
              />
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default AboutUs;

// ERP Solutions Section
// ERP Solutions Section
const ERPSolutionsSection = () => {
  const solutions = [
    {
      icon: <FaGraduationCap />,
      title: "Student Management",
      description:
        "Complete student lifecycle management from admission to alumni relations, including attendance tracking, performance monitoring, and communication.",
    },
    {
      icon: <FaLaptopCode />,
      title: "Fee Management",
      description:
        "Automated fee collection, overdue tracking, payment gateway integration, and comprehensive financial reporting with customizable fee structures.",
    },
    {
      icon: <FaUsers />,
      title: "Staff Management",
      description:
        "Complete staff records, attendance tracking, payroll management, performance evaluation, and role-based access control.",
    },
    {
      icon: <FaCogs />,
      title: "Timetable & Scheduling",
      description:
        "Intelligent scheduling system that prevents conflicts, optimizes resource allocation, and allows for easy adjustments and notifications.",
    },
    {
      icon: <FaChartBar />,
      title: "Analytics Dashboard",
      description:
        "Comprehensive data visualization and reporting tools that provide actionable insights into institutional performance and student outcomes.",
    },
    {
      icon: <FaShieldAlt />,
      title: "Secure Access Control",
      description:
        "Role-based permissions system ensuring that sensitive information is only accessible to authorized personnel with complete audit logging.",
    },
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.3,
      },
    },
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { duration: 0.5 },
    },
  };

  return (
    <section className="about-section about-section--alt">
      <div className="about-container">
        <motion.div
          className="section-header"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
        >
          <span className="section-badge">What We Offer</span>
          <h2>Our ERP Solutions</h2>
          <p className="section-description">
            Comprehensive modules designed to streamline every aspect of
            educational institution management
          </p>
        </motion.div>

        <motion.div
          className="erp-solutions-grid"
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
        >
          {solutions.map((solution, index) => (
            <motion.div
              key={index}
              className="feature-card"
              variants={itemVariants}
            >
              <div className="feature-header">
                <div className="feature-icon">{solution.icon}</div>
                <h3>{solution.title}</h3>
              </div>
              <p>{solution.description}</p>

              {/* Optional - Add this if you want a "Learn More" link */}
              {/* <div className="feature-footer">
                <a href="#" className="learn-more">
                  Learn more <FaArrowRight size={14} />
                </a>
              </div> */}
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};
